import { useState, useRef, useEffect, TouchEvent } from "react";
import { Button } from "@/components/ui/button";
import { ChevronRight, ChevronLeft, X } from "lucide-react";
import { 
  DemoEventCard, 
  DemoRSVPFlow, 
  DemoBuddyMatch, 
  DemoChatPreview,
  DemoWelcome,
  DemoHerds,
  DemoFriends,
  DemoFinale,
  DemoEventTypes,
  DemoTiers,
  DemoLegendaryTeaser,
  DemoMeganWelcome
} from "./demo";
import { useTourStore, ShowcaseSlide } from "@/hooks/useTour";
import { tourSteps } from "./tourSteps";

interface TourShowcaseOverlayProps {
  slides: ShowcaseSlide[];
  onComplete: () => void;
}

export const TourShowcaseOverlay = ({ slides, onComplete }: TourShowcaseOverlayProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [key, setKey] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  const { endTour, currentStepIndex, previousStep } = useTourStore();
  
  // Swipe gesture state
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const minSwipeDistance = 50;

  // Calculate total steps for display
  const totalTourSteps = tourSteps.length;
  const currentTourStep = currentStepIndex + 1;

  // Auto-hide swipe hint after 4 seconds
  useEffect(() => {
    if (showSwipeHint) {
      const timer = setTimeout(() => {
        setShowSwipeHint(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showSwipeHint]);

  // Reset slide index when step changes
  useEffect(() => {
    setCurrentSlide(0);
    setKey(prev => prev + 1);
  }, [currentStepIndex]);

  const triggerHaptic = (type: 'light' | 'medium' | 'success' = 'light') => {
    import('despia-native').then(({ default: despia }) => {
      const hapticType = type === 'success' ? 'haptic://success' : type === 'medium' ? 'haptic://medium' : 'haptic://';
      despia(hapticType);
    }).catch(() => {});
  };

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      triggerHaptic('light');
      setSlideDirection('left');
      setCurrentSlide(currentSlide + 1);
      setKey(prev => prev + 1);
    } else {
      triggerHaptic('success');
      onComplete();
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      // Navigate to previous slide within current step
      triggerHaptic('light');
      setSlideDirection('right');
      setCurrentSlide(currentSlide - 1);
      setKey(prev => prev + 1);
    } else if (currentStepIndex > 0) {
      // Navigate to previous tour step
      triggerHaptic('light');
      setSlideDirection('right');
      previousStep();
    }
  };

  const handleDotClick = (index: number) => {
    if (index !== currentSlide) {
      triggerHaptic('light');
      setSlideDirection(index > currentSlide ? 'left' : 'right');
      setCurrentSlide(index);
      setKey(prev => prev + 1);
    }
  };

  const handleSkip = () => {
    triggerHaptic('medium');
    endTour();
  };

  // Touch handlers for swipe gestures
  const onTouchStart = (e: TouchEvent) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe || isRightSwipe) {
      setShowSwipeHint(false);
    }
    
    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrev();
    }
    
    touchStartX.current = null;
    touchEndX.current = null;
  };

  const getSlideAnimation = () => {
    if (!slideDirection) return 'animate-fade-in';
    return slideDirection === 'left' ? 'animate-slide-in-left' : 'animate-slide-in-right';
  };

  const renderComponent = (componentType: string) => {
    switch (componentType) {
      case 'event-card':
        return <DemoEventCard key={key} />;
      case 'rsvp-form':
        return <DemoRSVPFlow key={key} />;
      case 'buddy-match':
        return <DemoBuddyMatch key={key} />;
      case 'chat-preview':
        return <DemoChatPreview key={key} />;
      case 'welcome':
        return <DemoWelcome key={key} />;
      case 'herds':
        return <DemoHerds key={key} />;
      case 'friends':
        return <DemoFriends key={key} />;
      case 'finale':
        return <DemoFinale key={key} />;
      case 'event-types':
        return <DemoEventTypes key={key} />;
      case 'tiers':
        return <DemoTiers key={key} />;
      case 'legendary-teaser':
        return <DemoLegendaryTeaser key={key} />;
      case 'megan-welcome':
        return <DemoMeganWelcome key={key} />;
      default:
        return <DemoWelcome key={key} />;
    }
  };

  // Guard against empty slides array
  if (!slides || slides.length === 0) {
    return null;
  }

  const slide = slides[currentSlide];

  // Guard against invalid slide index
  if (!slide) {
    return null;
  }

  // Show step dots only if there are multiple slides in this step
  const showSlideDots = slides.length > 1;

  return (
    <div 
      className="fixed inset-x-0 top-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      style={{
        paddingTop: 'calc(var(--safe-area-top) + 1rem)',
        bottom: 'calc(4rem + var(--safe-area-bottom, 0px))',
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Close button */}
      <button
        onClick={handleSkip}
        className="absolute right-4 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-white/70 hover:text-white transition-colors"
        style={{ top: 'calc(var(--safe-area-top) + 1rem)' }}
        aria-label="Skip tour"
      >
        <X className="w-6 h-6" />
      </button>

      <div className="w-full max-w-md flex flex-col items-center gap-5">
        {/* Step indicator dots - show tour step progress */}
        <div className="flex items-center gap-2">
          {tourSteps.map((_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentStepIndex 
                  ? 'w-6 bg-primary' 
                  : index < currentStepIndex 
                    ? 'w-2 bg-primary/50' 
                    : 'w-2 bg-white/30'
              }`}
            />
          ))}
        </div>

        {/* Step counter */}
        <p className="text-sm text-primary font-medium">
          Step {currentTourStep} of {totalTourSteps}
        </p>

        {/* Title */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white">{slide.title}</h2>
          <p className="text-white/70 mt-1">{slide.description}</p>
        </div>

        {/* Demo Component with slide animation */}
        <div className="w-full relative">
          <div className={`overflow-hidden ${getSlideAnimation()}`} key={key}>
            {renderComponent(slide.component)}
          </div>
          
          {/* Swipe hint - only on first slide and before dismissed */}
          {showSlideDots && currentSlide === 0 && showSwipeHint && (
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-2 text-white/50 text-xs animate-pulse transition-opacity duration-300">
              <ChevronLeft className="w-4 h-4" />
              <span>Swipe to navigate</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          )}
        </div>

        {/* Slide dots for multi-slide steps */}
        {showSlideDots && (
          <div className="flex items-center gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => handleDotClick(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide 
                    ? 'w-4 bg-white' 
                    : 'bg-white/40'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center gap-3 w-full">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentSlide === 0 && currentStepIndex === 0}
            className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20 disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <Button
            onClick={handleNext}
            className="flex-1 font-semibold"
          >
            {currentSlide === slides.length - 1 && currentStepIndex === tourSteps.length - 1 
              ? 'Finish' 
              : 'Next'}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* Skip link */}
        <button
          onClick={handleSkip}
          className="text-sm text-white/50 hover:text-white/70 transition-colors"
        >
          Skip tour
        </button>
      </div>
    </div>
  );
};
